/**
 * ReasoningEngine — Motor de razonamiento clínico siempre activo.
 *
 * Escucha el bus de eventos clínicos (FINDING_REVEALED, EXAM_ORDERED) y
 * recalcula patrones + triage en tiempo real. Es el puente entre las
 * acciones del alumno y el estado clínico del paciente.
 *
 * Sin este motor, los paneles educativos quedan desconectados: ordenar
 * un examen revelaba nodos pero nunca disparaba re-evaluación del triage
 * ni detección de nuevos patrones (sepsis, STEMI, etc.).
 */

import state from '../core/state';
import { PatternMatcher } from './PatternMatcher';
import { TriageClassifier } from './TriageClassifier';
import { Patient } from './Patient';

export class ReasoningEngine {
  private unsubscribers: Array<() => void> = [];
  private patient: Patient | null = null;
  private reevalTimer: number | null = null;

  constructor(
    private patternMatcher: PatternMatcher,
    private triageClassifier: TriageClassifier
  ) {}

  /**
   * Asocia el paciente activo. Se llama en cada loadPatient() porque el
   * grafo 3D se recrea completo al cambiar de caso.
   */
  setPatient(patient: Patient): void {
    this.patient = patient;
    this.patternMatcher.clearDetections();
  }

  /**
   * Suscribe al bus de eventos. Debe llamarse una sola vez en initialize().
   */
  start(): void {
    this.stop();

    this.unsubscribers.push(
      state.onEvent('FINDING_REVEALED', () => this.scheduleReevaluation())
    );

    // Un vital que cambia (deterioro o mejoría) obliga a re-evaluar patrones
    // y triage aunque el nodo ya estuviera revelado de antes.
    this.unsubscribers.push(
      state.onEvent('VITAL_CHANGED', () => this.scheduleReevaluation())
    );

    this.unsubscribers.push(
      state.onEvent('EXAM_ORDERED', (e) => {
        if (e.type !== 'EXAM_ORDERED') return;
        console.log(`[Reasoning] Examen ordenado: ${e.examLabel} (${e.nodeIds.length} nodos)`);
      })
    );
  }

  stop(): void {
    this.unsubscribers.forEach(fn => fn());
    this.unsubscribers = [];
    if (this.reevalTimer !== null) {
      clearTimeout(this.reevalTimer);
      this.reevalTimer = null;
    }
  }

  /**
   * Agrupa múltiples findings en una sola re-evaluación (cuando un examen
   * revela varios nodos casi simultáneamente, evita N pasadas innecesarias).
   */
  private scheduleReevaluation(): void {
    if (this.reevalTimer !== null) return;
    this.reevalTimer = window.setTimeout(() => {
      this.reevalTimer = null;
      this.reevaluate();
    }, 50);
  }

  /**
   * Re-ejecuta PatternMatcher y TriageClassifier sobre el estado actual
   * del paciente y propaga los resultados al state (que a su vez emite
   * PATTERN_DETECTED y TRIAGE_CHANGE para el resto de la UI).
   */
  private reevaluate(): void {
    if (!this.patient || !state.currentPatient) return;

    const nodes = Array.from(this.patient.nodes.values()).map(n => n.data);
    state.currentPatient.nodes = nodes;

    // Patrones clínicos (Beck, Cushing, Sepsis-3, STEMI, etc.).
    // evaluate() ya incorpora las reglas especializadas internamente.
    const detected = this.patternMatcher.evaluate(nodes);
    detected.forEach(p => state.addDetectedPattern(p));

    // 2. Triage recalculado según los datos disponibles ahora
    const assessment = this.triageClassifier.classify(state.currentPatient, 'auto');
    if (assessment.priority !== state.currentPatient.triagePriority) {
      state.updatePatientPriority(assessment.priority);
    }
  }
}

export default ReasoningEngine;
